// Éléments communs du module Documentation : libellés, types de réponse, sous-navigation.
import { useState } from 'react';
import { NavLink } from 'react-router';
import { ScanText } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useEnLigne } from '../../lib/preferences.ts';
import { Alerte, Badge, Bouton, useBarreDefilante, type Ton } from '../../components/ui.tsx';

export const TYPES_DOCUMENT: Record<string, string> = {
  fiche_produit: 'Fiche produit',
  notice_installation: 'Notice d’installation',
  manuel_configuration: 'Manuel de configuration',
  schema: 'Schéma',
  liste_points: 'Liste de points et registres',
  compatibilite: 'Compatibilité',
  note_version: 'Note de version',
  codes_defaut: 'Codes défaut',
  procedure_maintenance: 'Procédure de maintenance',
  avis_securite: 'Avis de sécurité',
  arret_commercialisation: 'Arrêt de commercialisation',
  demande_client: 'Demande du client',
  cctp: 'CCTP',
  plan: 'Plan',
  dpgf: 'DPGF',
  bordereau: 'Bordereau',
  devis_existant: 'Devis existant',
  avenant: 'Avenant',
  echange: 'Échange',
  photo: 'Photo',
  autre: 'Autre',
};

/** Types proposés à l'import dans la bibliothèque multimarque (les pièces de dossier s'importent depuis le dossier). */
export const TYPES_BIBLIOTHEQUE = ['fiche_produit', 'notice_installation', 'manuel_configuration', 'schema', 'liste_points', 'compatibilite',
  'note_version', 'codes_defaut', 'procedure_maintenance', 'avis_securite', 'arret_commercialisation', 'autre'];

export const LANGUES: Record<string, string> = { fr: 'Français', en: 'Anglais', de: 'Allemand', it: 'Italien', es: 'Espagnol', nl: 'Néerlandais', autre: 'Autre' };

export const libelleType = (t?: string | null) => (t ? TYPES_DOCUMENT[t] ?? t : 'Type inconnu');

export const STATUTS_COUVERTURE: Record<string, { libelle: string; ton: Ton }> = {
  disponible: { libelle: 'Disponible', ton: 'succes' },
  incomplet: { libelle: 'Incomplet', ton: 'ambre' },
  non_valide: { libelle: 'Non validé', ton: 'ambre' },
  obsolete: { libelle: 'Obsolète', ton: 'danger' },
  acces_a_obtenir: { libelle: 'Accès à obtenir', ton: 'neutre' },
};
export const BadgeCouverture = ({ statut }: { statut: string }) => {
  const s = STATUTS_COUVERTURE[statut] ?? { libelle: statut, ton: 'neutre' as Ton };
  return <Badge ton={s.ton}>{s.libelle}</Badge>;
};

export const LIBELLES_TACHE: Record<string, string> = {
  'document.extraire': 'Lecture et indexation d’un document',
  'document.lire_optique': 'Lecture optique des pages scannées (assistance)',
  'dossier.analyser': 'Analyse d’un dossier',
};

/**
 * Pages scannées restant sans texte : « Relancer la lecture » (référent / administrateur), par exemple après l'activation
 * de l'assistance. Bouton visible mais désactivé, avec la raison, si la lecture optique n'est pas possible.
 */
export function RelanceLecture({ versionId, lecture, relue }: {
  versionId: string; lecture: { pagesALire: number; peutRelancer: boolean; possible: boolean; raison: string | null }; relue: () => void;
}) {
  const enLigne = useEnLigne();
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState<{ ton: 'succes' | 'danger'; texte: string } | null>(null);
  if (!lecture.pagesALire && !message) return null;
  const relancer = async () => {
    setEnvoi(true); setMessage(null);
    try {
      const r = await api.post<{ pages: number }>(`/api/documents/versions/${versionId}/relire`);
      setMessage({ ton: 'succes', texte: `Lecture relancée pour ${r.pages} page(s). Le texte apparaîtra à la fin du traitement.` });
      relue();
    } catch (e) { setMessage({ ton: 'danger', texte: messageErreur(e) }); } finally { setEnvoi(false); }
  };
  if (message) return <Alerte ton={message.ton}>{message.texte}</Alerte>;
  const pages = `${lecture.pagesALire} page(s) sans texte (scannées) : leur contenu n’est ni cherchable ni cité.`;
  if (!lecture.peutRelancer) {
    return <Alerte ton="ambre" titre="Pages non lues">{pages} Un référent technique peut relancer leur lecture.</Alerte>;
  }
  const raison = !enLigne ? 'La lecture optique nécessite le réseau.' : !lecture.possible ? lecture.raison : null;
  return (
    <Alerte ton="ambre" titre="Pages non lues"
      action={<Bouton compact icone={<ScanText aria-hidden />} chargement={envoi} disabled={!!raison} onClick={relancer}
        aria-describedby={raison ? `relance-${versionId}` : undefined}>Relancer la lecture</Bouton>}>
      {pages}{raison && <span id={`relance-${versionId}`}> {raison}</span>}
    </Alerte>
  );
}
/** Libellé lisible d'un type de tâche ; jamais le code technique. */
export const libelleTache = (kind: string) => LIBELLES_TACHE[kind] ?? 'Traitement de fond';

export const tailleFichier = (o?: number | string | null) => {
  const n = Number(o);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / 1024 / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
};

export interface Version {
  id: string; document_id: string; version_label: string; file_name: string; mime: string; size_bytes: number | string; page_count: number | null;
  status: string; status_detail: string | null; collected_at: string; last_checked_at: string | null; reviewed_at: string | null;
  created_at: string; relecteur: string | null; empreinte?: string;
}

export interface Document {
  id: string; scope: 'bibliotheque' | 'dossier' | 'site'; title: string; doc_type: string; manufacturer: string | null; range_name: string | null;
  product_refs: string[]; versions_covered: string | null; protocols: string[]; language: string | null; region: string | null;
  provenance: string | null; rights: string | null; published_on: string | null; demo: boolean; created_at: string;
}

export interface Cadre { dossier?: { id: string; reference: string; title: string }; site?: { id: string; name: string } }

/** Sous-navigation du module (liens, pas des onglets : chaque vue a son adresse). */
export function NavDocumentation({ peutImporter }: { peutImporter: boolean }) {
  const liens = [
    { to: '/documentation', libelle: 'Bibliothèque', fin: true },
    { to: '/documentation/couverture', libelle: 'Couverture' },
    { to: '/documentation/traitements', libelle: 'Traitements' },
    ...(peutImporter ? [{ to: '/documentation/importer', libelle: 'Importer' }] : []),
  ];
  // Sur téléphone, la barre défile : fondu sur le bord où il reste des liens, lien actif ramené dans la vue.
  const ref = useBarreDefilante<HTMLElement>();
  return (
    <nav ref={ref} className="doc-sousnav defilante" aria-label="Documentation">
      {liens.map((l) => <NavLink key={l.to} to={l.to} end={l.fin} className="doc-sousnav-lien">{l.libelle}</NavLink>)}
    </nav>
  );
}
